import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import LoadingOverlay from '../LoadingOverlay';
import Toast from '../Toast';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const WaiterHome = ({ waiterId, restaurantId: restaurantIdProp, onNavigate }) => {
    // Normalize restaurantId to ensure it's always a string
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [stats, setStats] = useState({
        assignedTablesCount: 0,
        activeOrdersCount: 0,
        completedOrdersToday: 0,
        totalOrdersToday: 0,
        totalOrdersDone: 0,
        avgServiceTimeMinutes: 0
    });
    const [attendance, setAttendance] = useState(null);
    const [pendingNotificationsCount, setPendingNotificationsCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    useEffect(() => {
        if (waiterId && restaurantId) {
            fetchDashboardData();
        }
    }, [waiterId, restaurantId]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [statsResponse, attendanceResponse, waiterRequestsResponse] = await Promise.all([
                axios.get(API_ENDPOINTS.ORDERS_WAITER_STATS, {
                    params: { waiterId, restaurantId }
                }),
                axios.get(API_ENDPOINTS.ATTENDANCE_GET, {
                    params: { staffId: waiterId, date: new Date().toISOString().split('T')[0] }
                }),
                axios.get(API_ENDPOINTS.WAITER_REQUESTS_BY_WAITER(waiterId, restaurantId)).catch(() => ({ data: [] }))
            ]);

            setStats(statsResponse.data);
            if (attendanceResponse.data && attendanceResponse.data.length > 0) {
                setAttendance(attendanceResponse.data[0]);
            }

            const requests = waiterRequestsResponse.data || [];
            const now = Date.now();
            const oneDayMs = 24 * 60 * 60 * 1000;
            const recentActiveCount = requests.filter((req) => {
                if (!req) return false;
                if (req.status === 'done') return false;
                if (!req.createdAt) return true;
                const createdTime = new Date(req.createdAt).getTime();
                return now - createdTime < oneDayMs;
            }).length;

            setPendingNotificationsCount(recentActiveCount);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleCheckIn = async (shift) => {
        setActionLoading(true);
        setLoadingMessage('Checking in...');
        try {
            await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKIN, {
                staffId: waiterId,
                shift,
                restaurantId
            });
            showToast('Checked in successfully');
            await fetchDashboardData();
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-in failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCheckOut = async () => {
        if (!attendance) return;
        setActionLoading(true);
        setLoadingMessage('Checking out...');
        try {
            await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKOUT, {
                staffId: waiterId,
                shift: attendance.shift
            });
            showToast('Checked out successfully');
            await fetchDashboardData();
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-out failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const getCurrentShift = () => {
        const hour = new Date().getHours();
        if (hour >= 9 && hour < 15) return 'MORNING';
        if (hour >= 15 && hour < 22) return 'EVENING';
        return 'NIGHT';
    };

    const currentShift = getCurrentShift();
    const isCheckedIn = attendance && !attendance.checkOutTime;

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
        );
    }

    return (
        <div className="relative">
            {actionLoading && <LoadingOverlay message={loadingMessage} />}

            {/* Shift Status Card */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg shadow-lg p-6 mb-6 text-white">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold mb-2">Today's Shift</h2>
                        <div className="flex items-center gap-2 mt-2">
                            {isCheckedIn ? (
                                <>
                                    <span className="text-2xl">🟢</span>
                                    <span className="font-semibold">Checked In</span>
                                    {attendance.checkInTime && (
                                        <span className="text-sm text-blue-100">
                                            Since {new Date(attendance.checkInTime).toLocaleTimeString()}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <span className="text-2xl">🔴</span>
                                    <span className="font-semibold">Not Checked In</span>
                                </>
                            )}
                        </div>
                    </div>
                    <div>
                        {!isCheckedIn ? (
                            <button
                                onClick={() => handleCheckIn(currentShift)}
                                className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                            >
                                Check In
                            </button>
                        ) : (
                            <button
                                onClick={handleCheckOut}
                                className="bg-red-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-600 transition-colors"
                            >
                                Check Out
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Assigned Tables</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.assignedTablesCount}</p>
                        </div>
                        <span className="text-4xl">🪑</span>
                    </div>
                    <button
                        onClick={() => onNavigate && onNavigate('tables')}
                        className="mt-4 text-blue-600 text-sm hover:underline"
                    >
                        View Tables →
                    </button>
                </div>

                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Active Orders</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.activeOrdersCount}</p>
                        </div>
                        <span className="text-4xl">📋</span>
                    </div>
                    <button
                        onClick={() => onNavigate && onNavigate('orders')}
                        className="mt-4 text-orange-600 text-sm hover:underline"
                    >
                        View Orders →
                    </button>
                </div>

                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Completed Today</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.completedOrdersToday}</p>
                        </div>
                        <span className="text-4xl">✅</span>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Orders Today</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.totalOrdersToday}</p>
                        </div>
                        <span className="text-4xl">📊</span>
                    </div>
                </div>
            </div>

            {/* Additional Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-indigo-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Orders Done</p>
                            <p className="text-3xl font-bold text-gray-800">{stats.totalOrdersDone || 0}</p>
                            <p className="text-xs text-gray-500 mt-1">All time</p>
                        </div>
                        <span className="text-4xl">🎯</span>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-teal-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Avg Service Time</p>
                            <p className="text-3xl font-bold text-gray-800">
                                {stats.avgServiceTimeMinutes ? `${stats.avgServiceTimeMinutes.toFixed(1)} min` : 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Per order</p>
                        </div>
                        <span className="text-4xl">⏱️</span>
                    </div>
                </div>
            </div>

            {/* Notifications Alert */}
            {pendingNotificationsCount > 0 && (
                <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-lg shadow-lg p-4 mb-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-3xl">🔔</span>
                            <div>
                                <h3 className="font-bold text-lg">You have {pendingNotificationsCount} new notification{pendingNotificationsCount > 1 ? 's' : ''}!</h3>
                                <p className="text-red-100 text-sm">Check your notifications tab</p>
                            </div>
                        </div>
                        <button
                            onClick={() => onNavigate && onNavigate('notifications')}
                            className="bg-white text-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors"
                        >
                            View Notifications
                        </button>
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button
                        onClick={() => onNavigate && onNavigate('notifications')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center relative"
                    >
                        {pendingNotificationsCount > 0 && (
                            <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                {pendingNotificationsCount}
                            </span>
                        )}
                        <span className="text-3xl block mb-2">🔔</span>
                        <span className="text-sm font-medium">Notifications</span>
                    </button>
                    <button
                        onClick={() => onNavigate && onNavigate('attendance')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center"
                    >
                        <span className="text-3xl block mb-2">📅</span>
                        <span className="text-sm font-medium">Attendance</span>
                    </button>
                    <button
                        onClick={() => onNavigate && onNavigate('tables')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center"
                    >
                        <span className="text-3xl block mb-2">🪑</span>
                        <span className="text-sm font-medium">Manage Tables</span>
                    </button>
                    <button
                        onClick={() => onNavigate && onNavigate('orders')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center"
                    >
                        <span className="text-3xl block mb-2">📋</span>
                        <span className="text-sm font-medium">Create Order</span>
                    </button>
                    <button
                        onClick={() => onNavigate && onNavigate('live')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center"
                    >
                        <span className="text-3xl block mb-2">🔴</span>
                        <span className="text-sm font-medium">Live Orders</span>
                    </button>
                </div>
            </div>

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default WaiterHome;

